"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight, EyeOff, MessageSquareWarning, ShieldCheck, Smartphone, Users } from "lucide-react";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { HeroVisual } from "@/components/hero-visual";
import { usePrefs } from "@/components/prefs-provider";

const fade = {
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
};

export default function LandingPage() {
  const { t } = usePrefs();

  return (
    <div className="grain overflow-x-hidden">
      <SiteHeader />

      <section className="hero-mesh relative min-h-[100svh] pt-20">
        <div className="relative z-[2] mx-auto grid max-w-6xl items-center gap-10 px-4 pb-16 pt-10 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 lg:pb-20 lg:pt-14">
          <div>
            <motion.p
              className="font-display text-5xl font-bold tracking-tight text-[var(--ink)] sm:text-6xl lg:text-7xl"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              Chati<span className="text-[var(--teal)]">Safe</span>
            </motion.p>
            <motion.h1
              className="mt-5 max-w-xl text-2xl font-semibold leading-snug text-[var(--ink)] sm:text-3xl"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08 }}
            >
              {t.landing.h1}
            </motion.h1>
            <motion.p
              className="mt-5 max-w-lg text-base leading-relaxed text-[var(--ink-soft)] sm:text-lg"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.14 }}
            >
              {t.landing.lead}
            </motion.p>
            <motion.div
              className="mt-8 flex flex-col gap-3 sm:flex-row"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Link href="/register" className="btn-primary">
                {t.landing.cta} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
              <a href="#how" className="btn-ghost">
                {t.landing.seeHow}
              </a>
            </motion.div>
            <motion.p
              className="mt-6 text-sm text-[var(--ink-soft)]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
            >
              {t.landing.setup}
            </motion.p>
          </div>
          <HeroVisual />
        </div>
      </section>

      <section className="border-y border-[var(--line)] bg-[var(--foam)]/50 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <motion.div {...fade} className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--teal)]">{t.landing.reality}</p>
            <h2 className="font-display mt-3 text-3xl font-bold sm:text-4xl">{t.landing.realityH2}</h2>
            <p className="mt-4 text-lg text-[var(--ink-soft)]">{t.landing.realityP}</p>
          </motion.div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { title: t.landing.pain1t, body: t.landing.pain1b },
              { title: t.landing.pain2t, body: t.landing.pain2b },
              { title: t.landing.pain3t, body: t.landing.pain3b },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.08 }}
                className="border-t-2 border-[var(--teal)] pt-5"
              >
                <h3 className="font-display text-xl font-bold">{item.title}</h3>
                <p className="mt-3 text-[var(--ink-soft)]">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <motion.div {...fade} className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--teal)]">{t.landing.how}</p>
            <h2 className="font-display mt-3 text-3xl font-bold sm:text-4xl">{t.landing.howH2}</h2>
          </motion.div>
          <div className="mt-14 grid gap-10 md:grid-cols-3">
            {[
              { icon: Users, step: "01", title: t.landing.step1t, body: t.landing.step1b },
              { icon: Smartphone, step: "02", title: t.landing.step2t, body: t.landing.step2b },
              { icon: ShieldCheck, step: "03", title: t.landing.step3t, body: t.landing.step3b },
            ].map((item, i) => (
              <motion.div key={item.step} {...fade} transition={{ ...fade.transition, delay: i * 0.1 }}>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--teal)] text-white">
                  <item.icon className="h-5 w-5" />
                </div>
                <p className="text-xs font-bold tracking-[0.2em] text-[var(--teal)]">{item.step}</p>
                <h3 className="font-display mt-2 text-xl font-bold">{item.title}</h3>
                <p className="mt-3 text-[var(--ink-soft)]">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="protection" className="bg-[var(--teal-deep)] py-20 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <motion.div {...fade} className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--mint)]">{t.landing.prot}</p>
            <h2 className="font-display mt-3 text-3xl font-bold sm:text-4xl">{t.landing.protH2}</h2>
          </motion.div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: MessageSquareWarning, title: t.landing.f1t, body: t.landing.f1b },
              { icon: EyeOff, title: t.landing.f2t, body: t.landing.f2b },
              { icon: Users, title: t.landing.f3t, body: t.landing.f3b },
              { icon: ShieldCheck, title: t.landing.f4t, body: t.landing.f4b },
              { icon: Smartphone, title: t.landing.f5t, body: t.landing.f5b },
              { icon: ShieldCheck, title: t.landing.f6t, body: t.landing.f6b },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.05 }}
                className="rounded-2xl border border-white/10 bg-white/5 p-5"
              >
                <f.icon className="h-5 w-5 text-[var(--mint)]" />
                <h3 className="mt-4 text-lg font-bold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <motion.div
            {...fade}
            className="overflow-hidden rounded-[2rem] bg-[linear-gradient(135deg,#0f6b5c,#0a4a40)] px-6 py-14 text-center text-white sm:px-12"
          >
            <h2 className="font-display text-3xl font-bold sm:text-4xl">{t.landing.ctaH2}</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/75">{t.landing.ctaP}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link href="/register" className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 font-semibold text-[var(--teal-deep)]">
                {t.landing.startTrial} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
              <Link href="/plans" className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-6 py-3 font-semibold text-white">
                {t.landing.viewPlans}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
